export function isIosLike(navigatorLike = {}) {
  const userAgent = String(navigatorLike.userAgent || "");
  const platform = String(navigatorLike.platform || "");
  const touchPoints = Number(navigatorLike.maxTouchPoints || 0);
  return (
    /iPad|iPhone|iPod/u.test(userAgent) ||
    (platform === "MacIntel" && touchPoints > 1)
  );
}

export function clientPlatform(navigatorLike = {}) {
  if (isIosLike(navigatorLike)) return "ios_webview";
  if (/Android/u.test(String(navigatorLike.userAgent || ""))) {
    return "android_webview";
  }
  return "other";
}

export function preferredVideoCodec({
  ios = false,
  supportedVideoCodecs = []
} = {}) {
  const codecs = supportedVideoCodecs.map((codec) =>
    String(codec || "").toUpperCase()
  );
  if (ios && (codecs.length === 0 || codecs.includes("H264"))) return "h264";
  if (codecs.includes("VP8")) return "vp8";
  if (codecs.includes("H264")) return "h264";
  return "vp8";
}

export const POST_CALL_PROCESSING_ESTIMATE =
  "通话已结束，回放、逐字稿和回忆会陆续整理。高峰时需要排队，可能等待较久；你可以先离开，稍后回来查看，无需重复发起通话。";

export function alphaCallLimitNotice(callMaxSeconds) {
  const minutes = Math.round(Number(callMaxSeconds) / 60);
  if (!Number.isInteger(minutes) || minutes <= 0) return "";
  return `测试版单次最多 ${minutes} 分钟。到时会自动结束并保存；想继续聊，可以回到小程序再发起一次。`;
}

export function callLimitWarning({ remainingMilliseconds, shown = [] } = {}) {
  const remaining = Number(remainingMilliseconds);
  const seen = new Set(shown);
  if (!Number.isFinite(remaining) || remaining <= 0) return null;
  if (remaining <= 60_000 && !seen.has("one_minute")) {
    return {
      key: "one_minute",
      message: "还有约 1 分钟，本次测试通话会自动结束并保存。"
    };
  }
  if (remaining <= 5 * 60_000 && !seen.has("five_minutes")) {
    return {
      key: "five_minutes",
      message: "还有约 5 分钟，本次测试通话会自动结束并保存。"
    };
  }
  return null;
}

export function maximumDurationEndCopy(callMaxSeconds) {
  const minutes = Math.round(Number(callMaxSeconds) / 60) || 45;
  return {
    title: `已到测试版 ${minutes} 分钟上限`,
    message:
      "这段通话正在保存和整理。想继续聊，可以回到小程序再发起一次。"
  };
}

export function recordingStatusLabel(recordingStatus) {
  const labels = {
    waiting: "等待录制",
    starting: "正在准备录制",
    recording: "录制中",
    degraded: "录制已中断，当前内容可能未保存",
    saving: "正在保存",
    completed: "已保存",
    failed: "录制失败"
  };
  return labels[recordingStatus] || labels.waiting;
}

export function shouldShowPostCallProcessingEstimate({
  recordingStatus = "waiting",
  hasExplicitMessage = false
} = {}) {
  return (
    hasExplicitMessage !== true &&
    ["starting", "recording", "degraded", "saving", "completed"].includes(
      String(recordingStatus || "")
    )
  );
}

export function classifyClientError(error) {
  const evidence = [error?.code, error?.name, error?.message]
    .map((value) => String(value || "").toUpperCase())
    .join(" ");
  if (/NOT_ALLOWED|PERMISSION|DENIED/u.test(evidence)) {
    return "MEDIA_PERMISSION_DENIED";
  }
  if (/NOT_READABLE|TRACK_START|DEVICE.*BUSY/u.test(evidence)) {
    return "MEDIA_DEVICE_BUSY";
  }
  if (/LOAD FAILED|FAILED TO FETCH|NETWORK|WEBSOCKET|GATEWAY|ICE/u.test(evidence)) {
    return "NETWORK_LOAD_FAILED";
  }
  if (/TOKEN|AUTH|UNEXPECTED_RESPONSE/u.test(evidence)) {
    return "RTC_AUTH_FAILED";
  }
  if (/CODEC|ENCOD|DECODE/u.test(evidence)) {
    return "MEDIA_CODEC_UNAVAILABLE";
  }
  if (/NOT_SUPPORTED|SYSTEM_NOT_SUPPORTED/u.test(evidence)) {
    return "SYSTEM_NOT_SUPPORTED";
  }
  return "RTC_CLIENT_FAILURE";
}

export function callStartFailureMessage(stage, code) {
  if (code === "MEDIA_PERMISSION_DENIED") {
    return "微信没有取得摄像头或麦克风权限。请允许后重新进入通话。";
  }
  if (code === "MEDIA_DEVICE_BUSY") {
    return "摄像头或麦克风正在被其他应用使用。关闭占用后重新进入通话。";
  }
  if (code === "SYSTEM_NOT_SUPPORTED") {
    return "当前微信或系统版本暂时不能进行视频通话，请升级后再试。";
  }
  if (stage === "media_capture" || stage === "local_preview") {
    return "本机画面和声音暂时没有准备好，请检查微信权限后重试。";
  }
  if (stage === "rtc_token") {
    return "网络暂时没有连上通话服务，请重试进入通话。";
  }
  if (stage === "rtc_join") {
    return "暂时没有连上这次通话，请检查网络后重试。";
  }
  if (stage === "rtc_publish") {
    return "你的画面和声音暂时没有发出去，请重试进入通话。";
  }
  return "通话暂时没有准备好，请重试进入。";
}

export function shouldOfferMediaRecovery({
  callActive = false,
  remoteTrackCount = 0,
  autoplayBlocked = false,
  audioContextState = "unknown"
} = {}) {
  if (!callActive || remoteTrackCount < 1) return false;
  return (
    autoplayBlocked ||
    audioContextState === "suspended" ||
    audioContextState === "interrupted"
  );
}
